import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const fmt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");

// Settings → Parcel tracking (17TRACK). Key is stored server-side; the Inv
// Packing dashboard uses it to detect delivered parcels every 4 hours.
export function TrackingCard() {
  const { t } = useT();
  const qc = useQueryClient();
  const [key, setKey] = useState("");
  const { data: st } = useQuery({ queryKey: ["tracking-settings"], queryFn: async () => (await api.get("/tracking/settings")).data });
  const save = useMutation({
    mutationFn: (patch) => api.put("/tracking/settings", patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tracking-settings"] }); qc.invalidateQueries({ queryKey: ["inv-packing-dashboard"] }); setKey(""); toast.success(t("Saved")); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const test = useMutation({
    mutationFn: () => api.post("/tracking/test-key"),
    onSuccess: () => toast.success(t("17TRACK accepted the key")),
    onError: (e) => toast.error(e?.response?.data?.detail || "Key test failed"),
  });
  const poll = useMutation({
    mutationFn: () => api.post("/tracking/poll-now"),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["tracking-settings"] }); const s = r.data; toast[s.error ? "error" : "success"](s.error ? `⚠ ${s.error}` : `${t("Registered")} ${s.registered} · ${t("checked")} ${s.checked} · ${s.delivered} ${t("delivered")}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  if (!st) return null;
  return (
    <div id="tracking" className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="tracking-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-2xl">📬</span>
        <h2 className="font-semibold text-lg">{t("Parcel tracking (17TRACK)")}</h2>
        {st.configured ? (
          <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-300 font-mono text-[11px]" data-testid="tracking-key-set">{st.from_env ? t("key from server config") : st.key_hint}</Badge>
        ) : (
          <Badge className="bg-red-50 text-red-800 border border-red-300 text-[11px]" data-testid="tracking-key-missing">{t("Key missing")}</Badge>
        )}
      </div>
      <p className="text-sm text-slate-600">{t("Every 4 hours the CRM asks 17TRACK where each shipped parcel is. Delivered parcels show up as “Arrived” on the Inventory Packing dashboard until someone marks them received.")}</p>
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="tracking-key-row">
        <Label className="text-xs font-semibold">{t("17TRACK API key")}</Label>
        <div className="flex flex-wrap gap-2">
          <Input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={t("paste the Security Key from api.17track.net → Settings")} className="max-w-md font-mono" data-testid="tracking-key-input" />
          <Button onClick={() => save.mutate({ api_key: key })} disabled={!key.trim() || save.isPending} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="tracking-key-save">{t("Save key")}</Button>
          <Button variant="outline" onClick={() => test.mutate()} disabled={test.isPending || !st.configured} data-testid="tracking-key-test">{test.isPending ? t("Testing…") : `🔌 ${t("Test")}`}</Button>
          {st.configured && !st.from_env && <Button variant="outline" onClick={() => save.mutate({ clear_key: true })} className="text-red-700 border-red-200" data-testid="tracking-key-clear">{t("Remove")}</Button>}
        </div>
        <p className="text-[11px] text-slate-500">{t("Create a free API account at api.17track.net, open Settings and copy the Security Key. Each tracking number is registered once (counts against your 17TRACK quota).")}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[11px] font-mono text-slate-500" data-testid="tracking-last-poll">
        <span>{t("last check")} {fmt(st.last_poll_at)}{st.last_poll ? ` · ${st.last_poll.error ? `⚠ ${st.last_poll.error}` : `${st.last_poll.registered} ${t("registered")} · ${st.last_poll.checked} ${t("checked")} · ${st.last_poll.delivered} ${t("delivered")}`}` : ""}</span>
        <Button size="sm" variant="outline" onClick={() => poll.mutate()} disabled={poll.isPending || !st.configured} data-testid="tracking-poll-now">{poll.isPending ? t("Checking…") : `🔄 ${t("Check now")}`}</Button>
      </div>
    </div>
  );
}
